/**
 * GuardianCompany — single-company detail. Shows the live Report Card
 * (score, RAG, per-domain breakdown), open documents/tasks/screening,
 * and the Vendor Management table when `vendor` is active.
 *
 * Actions on this screen mutate the in-memory data store and the score
 * recalculates instantly.
 */
import { useMemo } from "react";
import { AlertTriangle, CheckCircle2, Clock, FileText, ShieldCheck, ToggleRight } from "lucide-react";
import {
  DOMAINS, MODULES, overallScore, domainScoreMap, rag, hasUnresolvedFlag, activeDomains,
} from "@/lib/guardianData";

const RAG_COLOR = { green: "#16a34a", amber: "#d97706", red: "#dc2626" };
const RAG_BG    = { green: "#dcfce7", amber: "#fef3c7", red: "#fee2e2" };

function scoreColor(s) {
  if (s === null) return "rgba(15,30,46,0.35)";
  if (s >= 80) return "#15803d";
  if (s >= 60) return "#a16207";
  return "#b91c1c";
}

export default function GuardianCompany({
  company, recalcStamp,
  onResolveScreening, onCompleteTask, onBumpTraining, onVendorAction,
}) {
  // Recompute scores on every render — cheap, deterministic, no memo needed.
  const overall = overallScore(company);
  const r = rag(overall);
  const domainMap = domainScoreMap(company);
  const domainsOn = new Set(activeDomains(company));
  const flagged = hasUnresolvedFlag(company);
  const vendorOn = company.activeModules.includes("vendor");

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header / Report Card top line */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ COMPANY · {company.vertical.toUpperCase()}</div>
          <h2 className="text-2xl font-semibold" style={{ color: "#0F1E2E" }}>{company.name}</h2>
          <div className="text-[10px] mt-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em", color: "rgba(15,30,46,0.5)" }}>
            {company.activeModules.length} active modules · {Array.from(domainsOn).length} domains scored
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>OVERALL</div>
          <div className="flex items-baseline gap-3 justify-end">
            <div className="text-4xl font-bold" style={{ color: scoreColor(overall) }} data-testid="co-score">{overall}<span className="text-base text-ink-900/40">/100</span></div>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: RAG_BG[r], color: RAG_COLOR[r], fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}>{r.toUpperCase()}</span>
          </div>
        </div>
      </div>

      {/* Red-flag protocol banner */}
      {flagged && (
        <div className="rounded-md p-3 flex items-center justify-between" style={{ background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.30)" }} data-testid="co-redflag-banner">
          <div className="flex items-center gap-2 text-sm" style={{ color: "#b91c1c" }}>
            <AlertTriangle className="h-4 w-4" /> <strong>Red flag protocol — UAR queue:</strong> unresolved screening flag. BSA/AML score is penalized until cleared.
          </div>
        </div>
      )}

      {/* Domain breakdown */}
      <div>
        <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ REPORT CARD · BY DOMAIN</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="co-domains">
          {DOMAINS.map((d) => {
            const s = domainMap[d];
            const off = s === null;
            return (
              <div key={d} className="rounded-md border p-3" style={{ background: off ? "rgba(15,30,46,0.03)" : "white", borderColor: off ? "rgba(15,30,46,0.10)" : `${scoreColor(s)}40` }}>
                <div className="text-[10px] font-semibold" style={{ color: off ? "rgba(15,30,46,0.45)" : "#0F1E2E" }}>{d}</div>
                <div className="text-xl font-bold mt-1" style={{ color: off ? "rgba(15,30,46,0.35)" : scoreColor(s), fontFamily: "ui-monospace, SF Mono, monospace" }}>
                  {off ? "Not configured" : `${s}/100`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Open items columns */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Documents */}
        <Block title="Documents" icon={FileText}>
          {company.documents.length === 0 && <Empty>No documents recorded.</Empty>}
          {company.documents.map((d) => (
            <Row key={d.name} title={d.name} meta={`${d.domain} · ${d.status === "in_review" ? `in review (${d.in_review_days}d)` : d.status === "expired" ? `expired ${d.expiresOn || ""}` : "current"}`} state={d.status === "expired" ? "red" : d.status === "in_review" ? "amber" : "green"} />
          ))}
        </Block>

        {/* Tasks */}
        <Block title="Open tasks" icon={Clock}>
          {company.tasks.length === 0 && <Empty>No open tasks.</Empty>}
          {company.tasks.map((t) => (
            <Row
              key={t.id}
              title={t.title}
              meta={`${t.domain} · ${t.severity} · ${t.overdue ? `overdue (due ${t.dueOn})` : `due ${t.dueOn}`}`}
              state={t.status === "done" ? "green" : t.overdue ? (t.severity === "high" ? "red" : "amber") : "amber"}
              done={t.status === "done"}
              action={t.status === "open" ? { label: "Complete", testId: `complete-${t.id}`, onClick: () => onCompleteTask(t.id) } : null}
            />
          ))}
        </Block>

        {/* Screening */}
        <Block title="Screening" icon={ShieldCheck}>
          {(!company.screening || company.screening.length === 0) && <Empty>No screening configured.</Empty>}
          {(company.screening || []).map((rec) => {
            const isFlag = !rec.resolved && (rec.ofac === "flag" || rec.pep === "flag" || rec.adverseMedia === "flag");
            const isReview = !rec.resolved && (rec.ofac === "review" || rec.pep === "review" || rec.adverseMedia === "review");
            const summary = [
              rec.ofac !== "clear" ? `OFAC: ${rec.ofac}` : null,
              rec.pep !== "clear" ? `PEP: ${rec.pep}` : null,
              rec.adverseMedia !== "clear" ? `Adverse media: ${rec.adverseMedia}` : null,
            ].filter(Boolean).join(" · ") || "All clear";
            return (
              <Row
                key={rec.id}
                title={rec.subject}
                meta={`${summary}${rec.resolved ? " · resolved" : ""}`}
                state={rec.resolved ? "green" : isFlag ? "red" : "amber"}
                done={rec.resolved}
                action={!rec.resolved && (isFlag || isReview) ? { label: "Resolve", testId: `resolve-${rec.id}`, onClick: () => onResolveScreening(rec.id) } : null}
              />
            );
          })}
        </Block>
      </div>

      {/* Training */}
      <div className="rounded-md border p-4 flex items-center justify-between" style={{ borderColor: "rgba(15,30,46,0.08)", background: "white" }}>
        <div>
          <div className="text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ TRAINING</div>
          <div className="text-sm mt-1" style={{ color: "#0F1E2E" }}>
            <strong>{company.training.completed}</strong> / {company.training.assigned} completed
            <span className="text-ink-900/55"> · {company.training.assigned > 0 ? Math.round(100 * company.training.completed / company.training.assigned) : 0}/100 score</span>
          </div>
        </div>
        <button
          onClick={onBumpTraining}
          className="px-3 py-1.5 rounded-md text-xs font-medium"
          style={{ background: "#0F1E2E", color: "#FDFBF5" }}
          data-testid="bump-training"
        >Mark 1 cohort complete</button>
      </div>

      {/* Vendor management */}
      {vendorOn && (
        <div>
          <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>
            / VENDOR MANAGEMENT · OCC 2023-17 · FRB SR 23-4 · FDIC FIL-29-2023
          </div>
          <div className="border rounded-md overflow-hidden" style={{ borderColor: "rgba(15,30,46,0.08)" }} data-testid="co-vendor-table">
            {company.vendors.length === 0 ? <Empty>No vendors recorded for this company yet.</Empty> : (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: "rgba(245,180,0,0.08)" }}>
                    <Th>Vendor</Th><Th>Tier</Th><Th>SOC 2</Th><Th>Re-attest due</Th><Th>Status</Th><Th align="right">Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {company.vendors.map((v, i) => (
                    <tr key={v.name} className={i % 2 ? "" : "bg-cream-50/40"} style={{ borderTop: "1px solid rgba(15,30,46,0.06)" }}>
                      <Td><strong>{v.name}</strong></Td>
                      <Td><Badge tier={v.tier} /></Td>
                      <Td><SocBadge soc2={v.soc2} /></Td>
                      <Td>{v.reattestDue}</Td>
                      <Td>{v.status}</Td>
                      <Td align="right">
                        <div className="inline-flex gap-1 justify-end flex-wrap">
                          {v.soc2 !== "current" && (
                            <button onClick={() => onVendorAction(v.name, "soc2_received")} className="px-2 py-1 text-[10px] rounded border hover:bg-cream-50" style={{ borderColor: "rgba(15,30,46,0.15)" }} data-testid={`v-soc-${v.name.replace(/\s+/g,"-").toLowerCase()}`}>Mark SOC 2 received</button>
                          )}
                          <button onClick={() => onVendorAction(v.name, "reattest_sent")} className="px-2 py-1 text-[10px] rounded border hover:bg-cream-50" style={{ borderColor: "rgba(15,30,46,0.15)" }}>Send re-attest</button>
                          {v.status !== "remediation" ? null : null}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Block({ title, icon: Icon, children }) {
  return (
    <div className="rounded-md border p-3" style={{ borderColor: "rgba(15,30,46,0.08)", background: "white" }}>
      <div className="flex items-center gap-2 text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>
        <Icon className="h-3 w-3" /> {title.toUpperCase()}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Empty({ children }) { return <div className="text-xs text-ink-900/40 py-1">{children}</div>; }

function Row({ title, meta, state = "amber", action, done }) {
  const color = state === "green" ? "#16a34a" : state === "red" ? "#dc2626" : "#d97706";
  return (
    <div className="flex items-start justify-between gap-2 text-xs">
      <div className="flex items-start gap-2 min-w-0">
        <span className="inline-block h-2 w-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: color }} />
        <div className="min-w-0">
          <div className="font-medium" style={{ color: "#0F1E2E", textDecoration: done ? "line-through" : "none", opacity: done ? 0.55 : 1 }}>{title}</div>
          <div className="text-[10px] text-ink-900/55">{meta}</div>
        </div>
      </div>
      {action && (
        <button onClick={action.onClick} data-testid={action.testId}
                className="text-[10px] px-2 py-0.5 rounded border whitespace-nowrap hover:bg-sun-500/10"
                style={{ borderColor: "rgba(245,180,0,0.6)", color: "#C98F00" }}>
          {action.label}
        </button>
      )}
      {done && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#16a34a" }} />}
    </div>
  );
}

function Th({ children, align }) { return <th className="text-left px-3 py-2 text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.16em", color: "rgba(15,30,46,0.55)", textAlign: align || "left" }}>{children}</th>; }
function Td({ children, align }) { return <td className="px-3 py-2" style={{ textAlign: align || "left", color: "#0F1E2E" }}>{children}</td>; }

function Badge({ tier }) {
  const map = { critical: ["#dc2626", "rgba(220,38,38,0.10)"], high: ["#d97706", "rgba(217,119,6,0.12)"], moderate: ["#0369a1", "rgba(3,105,161,0.10)"], low: ["#52525b", "rgba(82,82,91,0.10)"] };
  const [fg, bg] = map[tier] || map.low;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: bg, color: fg, fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}>{tier.toUpperCase()}</span>;
}
function SocBadge({ soc2 }) {
  const map = { current: ["#16a34a", "rgba(22,163,74,0.10)"], expired: ["#dc2626", "rgba(220,38,38,0.10)"], missing: ["#dc2626", "rgba(220,38,38,0.10)"] };
  const [fg, bg] = map[soc2] || map.current;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: bg, color: fg, fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}>{soc2.toUpperCase()}</span>;
}
